import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useState, useCallback, cloneElement, Children } from "react";
import PropTypes from "prop-types";
import { isNil } from "lodash/fp";
import { Icon } from "../icon/icon.es.js";
import { Text } from "../text/text.es.js";
import { ItemGroup, Item, CollapseItemIcon, ItemPrefixAction, ItemIcon, ItemContent, ItemSuffixIcon, ItemActions, ItemAction, ItemChildren } from "./menu-item.styles.es.js";
import { deprecated } from "../../prop-types-validators/deprecated.es.js";
const propTypes = {
  actions: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node).isRequired
  ]),
  as: PropTypes.elementType,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node).isRequired
  ]),
  collapsable: deprecated(PropTypes.bool, "Use collapsible instead."),
  collapsible: PropTypes.bool,
  content: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node).isRequired
  ]).isRequired,
  defaultCollapsed: PropTypes.bool,
  disabled: PropTypes.bool,
  header: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onClick: PropTypes.func,
  prefix: PropTypes.node,
  secondary: PropTypes.string,
  suffixIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  variant: PropTypes.oneOf(["accent", "danger", "default", "focused"])
};
const defaultProps = {
  actions: void 0,
  as: void 0,
  children: void 0,
  collapsable: void 0,
  collapsible: false,
  defaultCollapsed: false,
  disabled: false,
  header: false,
  icon: void 0,
  onClick: void 0,
  prefix: void 0,
  secondary: void 0,
  suffixIcon: void 0,
  variant: "default"
};
const MenuItem = forwardRef(
  ({
    actions,
    as,
    children,
    collapsable,
    collapsible: collapsibleProp,
    content,
    defaultCollapsed,
    disabled,
    header,
    icon,
    onClick,
    prefix,
    secondary,
    suffixIcon,
    variant,
    ...restProps
  }, ref) => {
    const collapsible = collapsibleProp || collapsable;
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const handleClick = useCallback(
      (event) => {
        if (collapsible) {
          setCollapsed((current) => !current);
        } else if (onClick) {
          onClick(event);
        }
      },
      [collapsible, onClick]
    );
    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          handleClick(event);
        }
      },
      [handleClick]
    );
    const clickable = (as || collapsible || !isNil(onClick)) && !disabled;
    return /* @__PURE__ */ jsxs(ItemGroup, { ref, children: [
      /* @__PURE__ */ jsxs(
        Item,
        {
          ...restProps,
          as,
          "aria-disabled": disabled,
          onClick: !disabled ? handleClick : void 0,
          onKeyDown: !disabled ? handleKeyDown : void 0,
          role: "button",
          tabIndex: clickable ? 0 : void 0,
          variant,
          $clickable: clickable,
          children: [
            collapsible && /* @__PURE__ */ jsx(CollapseItemIcon, { children: /* @__PURE__ */ jsx(Icon, { type: collapsed ? "right" : "down" }) }),
            prefix && /* @__PURE__ */ jsx(ItemPrefixAction, { children: cloneElement(prefix, {
              stopPropagation: true,
              preventDefault: true
            }) }),
            icon && /* @__PURE__ */ jsx(ItemIcon, { $disabled: disabled, variant, children: typeof icon === "string" ? /* @__PURE__ */ jsx(Icon, { magnitude: "small", type: icon }) : icon }),
            /* @__PURE__ */ jsx(ItemContent, { header, $disabled: disabled, variant, children: content }),
            secondary && /* @__PURE__ */ jsx(Text, { tone: "secondary", children: secondary }),
            suffixIcon && /* @__PURE__ */ jsx(ItemSuffixIcon, { $disabled: disabled, variant, children: typeof suffixIcon === "string" ? /* @__PURE__ */ jsx(Icon, { magnitude: "small", type: suffixIcon }) : suffixIcon }),
            actions && /* @__PURE__ */ jsx(ItemActions, { children: Children.map(actions, (child) => /* @__PURE__ */ jsx(ItemAction, { children: cloneElement(child, {
              stopPropagation: true,
              preventDefault: true
            }) })) })
          ]
        }
      ),
      children && !collapsed && /* @__PURE__ */ jsx(ItemChildren, { children })
    ] });
  }
);
MenuItem.propTypes = propTypes;
MenuItem.defaultProps = defaultProps;
export {
  MenuItem
};
